import { resolveFileUrl } from "@/lib/api";

/**
 * schema.org JSON-LD builders for Gate + Gather.
 *
 * These are additive metadata — pure data, no runtime effects. Consumed by
 * the <Seo jsonLd={…}/> prop which injects one <script type="application/
 * ld+json"> block per array entry. Kept in one place so schema names + shapes
 * stay consistent across the marketing site and storefronts.
 *
 * Canonical origin: always the apex domain (no www) so Google merges the
 * knowledge-panel signal with the sitemap + canonical <link>.
 */

const ORIGIN = "https://gateandgather.co.nz";

// Reused across Product Offers so Google's Merchant Listing / Rich Results
// Test doesn't flag missing "priceValidUntil". One year out is the common
// pattern for evergreen inventory.
const oneYearFromNow = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split("T")[0];
};

/** Global Organization schema — landing + any page representing the platform. */
export const gateAndGatherOrganization = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Gate + Gather",
  url: ORIGIN,
  logo: `${ORIGIN}/apple-touch-icon.svg`,
  slogan: "Simple selling for independent growers",
  description:
    "Gate + Gather is a simple online-selling platform for micro plant growers. Each grower gets their own storefront (a Gate) with live availability, prices and online or offline payment.",
  areaServed: { "@type": "Country", name: "New Zealand" },
  sameAs: [
    "https://www.instagram.com/gateandgather/",
    "https://www.facebook.com/gateandgather",
  ],
});

/** Global WebSite schema — enables Google's sitelinks search box treatment. */
export const gateAndGatherWebSite = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Gate + Gather",
  url: ORIGIN,
  potentialAction: {
    "@type": "SearchAction",
    target: `${ORIGIN}/g/{search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

/**
 * BreadcrumbList — improves Search Console breadcrumb display on non-Gate
 * marketing pages. `trail` is an array of {name, path} in order.
 */
export const breadcrumb = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.name,
    item: `${ORIGIN}${t.path}`,
  })),
});

/**
 * FAQPage JSON-LD — Google shows FAQ rich results when a page marks up
 * question/answer pairs. `qas` is an array of {question, answer} in plain
 * text (answers may contain simple HTML strings but no scripts).
 */
export const faqPage = (qas) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qas.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

/**
 * Per-seller LocalBusiness schema. Represents an individual grower's Gate
 * as a real local business Google can show in a knowledge panel or map card.
 * `slug` is required; everything else falls back gracefully when missing.
 */
export const storeLocalBusiness = (store, slug) => {
  const url = `${ORIGIN}/g/${slug}`;
  const img =
    store?.cover_url ? resolveFileUrl(store.cover_url) : `${ORIGIN}/apple-touch-icon.svg`;
  const logo = store?.logo_url ? resolveFileUrl(store.logo_url) : undefined;
  const areaServed = store?.location
    ? { "@type": "Place", name: store.location }
    : undefined;
  const doc = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": url,
    name: store?.business_name,
    url,
    image: img,
    description:
      store?.intro ||
      `Independent grower selling on Gate + Gather${
        store?.location ? ` in ${store.location}` : ""
      }.`,
    address: store?.location
      ? { "@type": "PostalAddress", addressLocality: store.location, addressCountry: "NZ" }
      : undefined,
    areaServed,
    logo,
    priceRange: "$",
    isPartOf: { "@type": "Organization", name: "Gate + Gather", url: ORIGIN },
  };
  // Trim undefined keys so the emitted JSON is clean.
  Object.keys(doc).forEach((k) => doc[k] === undefined && delete doc[k]);
  return doc;
};

/**
 * Product schema for a single product on a seller's Gate. Only rendered for
 * products in an offerable state (available OR sold_out — Google still
 * indexes sold-out with OutOfStock availability). `coming_soon` is skipped
 * because it would misrepresent availability.
 *
 * Includes hasMerchantReturnPolicy + shippingDetails so Google Merchant
 * Listings doesn't flag them as missing (both are required for rich
 * results eligibility as of 2024).
 */
export const productOffer = (product, store, slug) => {
  if (!product || !store) return null;
  const state = product.state || "available";
  if (state === "coming_soon") return null; // don't emit misleading offer
  const url = `${ORIGIN}/g/${slug}`;
  // Product image field on the API is `photo_url`. Fall back to the store
  // cover so every Product entry has an image — Google requires an image
  // for Merchant Listings rich results and blocks eligibility without one.
  const rawImg = product.photo_url || product.image_url || store?.cover_url;
  const image = rawImg ? (rawImg.startsWith("http") ? rawImg : resolveFileUrl(rawImg)) : undefined;
  const availability =
    state === "sold_out" || (product.quantity || 0) <= 0
      ? "https://schema.org/OutOfStock"
      : "https://schema.org/InStock";

  // Pickup-first model: growers ship locally or take pickup at their Gate.
  // A zero-cost NZ-local shipping row keeps Google happy without misrepresenting
  // service. Returns are handled seller-to-buyer (not by Gate + Gather), so
  // MerchantReturnNotPermitted is the honest category.
  const shippingDetails = {
    "@type": "OfferShippingDetails",
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "NZ",
    },
    shippingRate: {
      "@type": "MonetaryAmount",
      value: "0",
      currency: "NZD",
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 3, unitCode: "DAY" },
      transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 7, unitCode: "DAY" },
    },
  };
  const hasMerchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "NZ",
    returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
  };

  const doc = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    // Prefer the seller's own description so the Product entry adds real
    // signal beyond the name. The auto-generated fallback is used only
    // when the seller hasn't filled one in.
    description:
      (product.description && product.description.trim()) ||
      `${product.name} — from ${store.business_name} on Gate + Gather.`,
    image,
    sku: product.id,           // stable per-product identifier
    itemCondition: "https://schema.org/NewCondition",
    brand: { "@type": "Brand", name: store.business_name },
    offers: {
      "@type": "Offer",
      priceCurrency: "NZD",
      price: Number(product.price || 0).toFixed(2),
      priceValidUntil: oneYearFromNow(),
      availability,
      itemCondition: "https://schema.org/NewCondition",
      url,
      seller: { "@type": "LocalBusiness", name: store.business_name, url },
      hasMerchantReturnPolicy,
      shippingDetails,
    },
  };
  Object.keys(doc).forEach((k) => doc[k] === undefined && delete doc[k]);
  return doc;
};

/**
 * Build the full JSON-LD payload for a storefront page: one LocalBusiness
 * for the Gate + one Product entry per non-coming-soon product.
 */
export const storeJsonLd = (store, products, slug) => {
  const out = [storeLocalBusiness(store, slug)];
  (products || []).forEach((p) => {
    const doc = productOffer(p, store, slug);
    if (doc) out.push(doc);
  });
  return out;
};
